import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PistaLiveDebugService } from '../services/pista-live-debug.service';
import { ExposicionCatalogoApiService } from './exposicion-catalogo-api.service';
import { ExposicionSelectionService } from './exposicion-selection.service';
import { PremiacionEventosPollingService } from './premiacion-eventos-polling.service';
import { PremiacionWsChangedEvent, PremiacionWsDebugService } from './premiacion-ws-debug.service';

@Injectable({
  providedIn: 'root'
})
export class PremiacionRealtimeSyncService {
  private initialized = false;
  private readonly changedSubject = new Subject<PremiacionWsChangedEvent>();
  private wsChangedSub: Subscription | null = null;
  private pollingSub: Subscription | null = null;
  private activeExposicionId: number | null = null;
  private pollingExpoId: number | null = null;
  private fallbackMonitorTimer: ReturnType<typeof setInterval> | null = null;
  private pistaLiveDebugStarted = false;
  private readonly fallbackPollingIntervalMs = this.resolvePositiveInt(
    environment.FEATURES?.['premiacionPollingIntervalMs'],
    10000
  );
  private readonly fallbackMonitorIntervalMs = 2000;

  constructor(
    private readonly premiacionWsDebugService: PremiacionWsDebugService,
    private readonly premiacionEventosPollingService: PremiacionEventosPollingService,
    private readonly pistaLiveDebugService: PistaLiveDebugService,
    private readonly exposicionSelectionService: ExposicionSelectionService,
    private readonly exposicionCatalogoApiService: ExposicionCatalogoApiService
  ) {}

  public initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.wsChangedSub = this.premiacionWsDebugService.changed$.subscribe((event) => {
      this.publishChanged(event);
    });

    const initialExposicionId = this.exposicionSelectionService.getSelectedExposicionId();
    if (initialExposicionId) {
      this.setActiveExposicion(initialExposicionId);
    }
  }

  public get changed$(): Observable<PremiacionWsChangedEvent> {
    return this.changedSubject.asObservable();
  }

  public setActiveExposicion(exposicionId: number | null): void {
    const nextExposicionId = this.normalizeExposicionId(exposicionId);
    if (!nextExposicionId) {
      void this.premiacionWsDebugService.stop();
      if (this.pistaLiveDebugStarted) {
        this.pistaLiveDebugStarted = false;
        void this.pistaLiveDebugService.stop();
      }
      this.stopFallbackMonitor();
      this.stopPolling();
      this.activeExposicionId = null;
      return;
    }

    this.exposicionSelectionService.setSelectedExposicionId(nextExposicionId);

    if (this.activeExposicionId === null) {
      this.activeExposicionId = nextExposicionId;
      void this.premiacionWsDebugService.start(nextExposicionId);
      this.pistaLiveDebugStarted = true;
      void this.pistaLiveDebugService.start(nextExposicionId);
      this.ensureFallbackMonitorRunning();
      this.syncFallbackPollingState();
      return;
    }

    if (this.activeExposicionId === nextExposicionId) {
      void this.premiacionWsDebugService.start(nextExposicionId);
      if (!this.pistaLiveDebugStarted) {
        this.pistaLiveDebugStarted = true;
        void this.pistaLiveDebugService.start(nextExposicionId);
      }
      this.ensureFallbackMonitorRunning();
      this.syncFallbackPollingState();
      return;
    }

    this.activeExposicionId = nextExposicionId;
    void this.premiacionWsDebugService.switchExpo(nextExposicionId);
    if (!this.pistaLiveDebugStarted) {
      this.pistaLiveDebugStarted = true;
      void this.pistaLiveDebugService.start(nextExposicionId);
    } else {
      void this.pistaLiveDebugService.switchExpo(nextExposicionId);
    }
    this.syncFallbackPollingState();
  }

  public destroy(): void {
    if (this.wsChangedSub) {
      this.wsChangedSub.unsubscribe();
      this.wsChangedSub = null;
    }
    this.stopFallbackMonitor();
    this.stopPolling();
    if (this.pistaLiveDebugStarted) {
      this.pistaLiveDebugStarted = false;
      void this.pistaLiveDebugService.stop();
    }
  }

  private publishChanged(event: PremiacionWsChangedEvent): void {
    this.exposicionCatalogoApiService.invalidateCatalogo(event.exposicionId);
    this.changedSubject.next(event);
  }

  private ensureFallbackMonitorRunning(): void {
    if (this.fallbackMonitorTimer !== null) {
      return;
    }

    this.fallbackMonitorTimer = setInterval(() => {
      this.syncFallbackPollingState();
    }, this.fallbackMonitorIntervalMs);
  }

  private stopFallbackMonitor(): void {
    if (this.fallbackMonitorTimer === null) {
      return;
    }

    clearInterval(this.fallbackMonitorTimer);
    this.fallbackMonitorTimer = null;
  }

  private syncFallbackPollingState(): void {
    const exposicionId = this.activeExposicionId;
    if (!exposicionId) {
      this.stopPolling();
      return;
    }

    if (this.premiacionWsDebugService.isConnectedToExpo(exposicionId)) {
      this.stopPolling();
      return;
    }

    this.startPolling(exposicionId);
  }

  private startPolling(exposicionId: number): void {
    if (this.pollingSub && this.pollingExpoId === exposicionId) {
      return;
    }

    this.stopPolling();
    this.pollingExpoId = exposicionId;
    this.pollingSub = this.premiacionEventosPollingService
      .watch(exposicionId, {
        intervalMs: this.fallbackPollingIntervalMs,
        // Forzamos un cursor explicito para evitar el "priming" que descartaba
        // justamente los eventos ocurridos mientras el websocket estuvo caido.
        initialEventoId: 0
      })
      .subscribe((batch) => {
        if (this.activeExposicionId !== exposicionId) {
          return;
        }

        if (this.premiacionWsDebugService.isConnectedToExpo(exposicionId)) {
          return;
        }

        this.publishChanged({
          exposicionId,
          payload: batch
        });
      });
  }

  private stopPolling(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
    this.pollingExpoId = null;
  }

  private normalizeExposicionId(value: number | null): number | null {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return null;
    }

    return Math.trunc(numeric);
  }

  private resolvePositiveInt(value: unknown, fallback: number): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return fallback;
    }

    return Math.trunc(numeric);
  }
}
